import { useMemo, useState, useEffect } from 'react';
import { Mic, Volume2, Hand, MousePointerClick } from 'lucide-react';

export default function VoiceOpenQuestion({ question, transcript, status, mode, onChoiceClick, taskState }) {
  const showOptions = useMemo(() => {
    if (!question.target || !question.images) return null;
    const others = Object.keys(question.images).filter(k => k !== question.target);
    const random3 = others.sort(() => 0.5 - Math.random()).slice(0, 3);
    return [...random3, question.target].sort(() => 0.5 - Math.random());
  }, [question.target, question.images]);

  const [timeLeft, setTimeLeft] = useState(question.showTimer || 0);
  
  useEffect(() => {
    if (question.showTimer && status === 'listening' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft, status, question.showTimer]);

  // ИНТЕРФЕЙС ДЛЯ БДИТЕЛЬНОСТИ (БЕЗ БУКВ НА ЭКРАНЕ)
  // Способ ответа (inputMode) и текст инструкции для touch (displayAsk)
  // выставляет ScaleRunner в taskState. Нет — значит хлопки.
  if (question.type === 'vigilance') {
    const inputMode = taskState?.inputMode || 'clap';
    const hint = {
      touch:   { icon: <Hand className="size-16 text-accent animate-bounce" />, text: 'Касайтесь экрана, услышав «А»' },
      pointer: { icon: <MousePointerClick className="size-16 text-accent animate-bounce" />, text: 'Отвечайте кликом мыши' },
      clap:    { icon: <Mic className="size-16 text-red-500 animate-bounce" />, text: 'Слушаю хлопки...' },
    }[inputMode] || { icon: <Mic className="size-16 text-red-500 animate-bounce" />, text: 'Слушаю хлопки...' };
    return (
      <div className="flex flex-col items-center justify-center gap-8 w-full max-w-5xl h-screen">
        <h2 className="text-xl md:text-2xl font-semibold text-primary text-center">
          {taskState?.displayAsk || question.ask}
        </h2>
        <div className="min-h-[150px] flex flex-col items-center justify-center gap-4">
          {status === 'speaking'
            ? <Volume2 className="size-16 text-accent animate-pulse" />
            : <>{hint.icon}<p className="text-xl text-muted-foreground">{hint.text}</p></>}
        </div>
      </div>
    );
  }

  // --- ЛОГИКА ДЛЯ СЕКЦИИ 4 (Картинки) ---
  if (question.target) {
    if (mode === 'say') {
      return (
        <div className="flex flex-col md:flex-row items-center justify-center gap-12 w-full max-w-5xl">
          <div className="flex-shrink-0 flex flex-col items-center gap-4">
            <h2 className="text-2xl md:text-3xl font-semibold text-primary text-center">{question.ask}</h2>
            <img src={question.images[question.target]} alt={question.target} className="max-h-[45vh] w-auto rounded-2xl shadow-lg border border-border object-contain" />
          </div>
          <div className="flex flex-col items-center justify-center gap-6 w-full md:w-1/3 min-h-[200px]">
            {status === 'speaking' && <Volume2 className="size-12 text-accent animate-pulse" />}
            {status === 'listening' && (
              <div className="flex flex-col items-center gap-4">
                <Mic className="size-16 text-red-500 animate-bounce" />
                <div className="px-6 py-3 bg-secondary rounded-2xl border-2 border-red-500/30 text-center min-h-[60px] flex items-center justify-center w-full">
                  <span className="text-4xl text-primary font-medium">{transcript || "Слушаю..."}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    if (mode === 'show' && showOptions) {
      return (
        <div className="flex flex-col items-center justify-center gap-6 w-full max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-semibold text-primary text-center min-h-[40px]">
            Покажите: {question.target}
          </h2>
          <div className="grid grid-cols-2 gap-4 md:gap-6 w-full">
            {showOptions.map((opt) => (
              <button key={opt} onClick={() => onChoiceClick(opt)} disabled={status === 'speaking' || status === 'processing'} className="bg-secondary border-2 border-border rounded-3xl p-2 hover:bg-accent/10 hover:border-accent transition-colors disabled:opacity-50 flex items-center justify-center h-32 md:h-44 overflow-hidden">
                <img src={question.images[opt]} alt={opt} className="max-h-full max-w-full object-contain rounded-xl" />
              </button>
            ))}
          </div>
          <div className="h-12 mt-2 flex items-center justify-center">
            {status === 'speaking' && <Volume2 className="size-10 text-accent animate-pulse" />}
          </div>
        </div>
      );
    }
    return null;
  }

  // --- СТАНДАРТНАЯ ЛОГИКА (Секции 1, 6, 8, 9, 11) ---
  return (
    <div className="flex flex-col md:flex-row items-center justify-center gap-12 w-full max-w-5xl">
      <div className="flex-shrink-0 flex flex-col items-center gap-4 w-full md:w-auto">
        <h2 className="text-2xl md:text-3xl font-semibold text-primary text-center min-h-[40px] max-w-2xl whitespace-pre-line">
          {question.ask}
        </h2>
        
        {question.imageUrl && (
          <img src={question.imageUrl} alt="Задание" className="max-h-[45vh] w-auto rounded-2xl shadow-lg border border-border object-contain" />
        )}

        {question.showTimer && (
          <div className="text-5xl font-bold text-accent my-4">
            {timeLeft}
          </div>
        )}

                {mode === 'show' && !question.imageUrl && question.emojiOptions && (
          <div className="grid grid-cols-2 gap-6 w-full max-w-2xl mt-4">
            {question.emojiOptions.map((opt) => (
              <button key={opt.label} onClick={() => onChoiceClick(opt.label)} disabled={status === 'speaking' || status === 'processing'} className="bg-secondary border-2 border-border rounded-3xl p-6 min-h-[180px] hover:bg-accent/10 hover:border-accent transition-colors disabled:opacity-50 flex flex-col items-center justify-center gap-3">
                <span className="text-7xl md:text-8xl leading-none">{opt.emoji}</span>
                <span className="text-xl md:text-2xl font-semibold text-primary">{opt.label}</span>
              </button>
            ))}
          </div>
        )}
        {mode === 'show' && !question.imageUrl && !question.emojiOptions && question.options && (
          <div className="grid grid-cols-2 gap-4 md:gap-6 w-full max-w-3xl mt-4">
            {question.options.map((opt) => (
              <button key={opt} onClick={() => onChoiceClick(opt)} disabled={status === 'speaking' || status === 'processing'} className="bg-secondary border-2 border-border rounded-3xl p-8 text-2xl md:text-3xl font-bold text-primary min-h-[100px] hover:bg-accent/10 hover:border-accent transition-colors disabled:opacity-50 flex items-center justify-center">
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {mode === 'say' && (
        <div className="flex flex-col items-center justify-center gap-6 w-full md:w-1/3 min-h-[200px]">
          {status === 'speaking' && <Volume2 className="size-12 text-accent animate-pulse" />}
          {status === 'listening' && (
            <div className="flex flex-col items-center gap-4">
              <Mic className="size-16 text-red-500 animate-bounce" />
              <div className="px-6 py-3 bg-secondary rounded-2xl border-2 border-red-500/30 text-center min-h-[60px] flex items-center justify-center w-full">
                <span className="text-4xl text-primary font-medium">{transcript || "Слушаю..."}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}